import { axiosRubyClient } from '@/util/axios/axiosClient';

// Types for Worker List
export interface WorkerUser {
  id: number;
  email: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  full_name: string;
  phone_number: string | null;
  date_of_birth: string | null;
  language: string;
  secondary_language: string | null;
  smartphone_type: string | null;
}

export interface WorkerStatuses {
  candidate_status: string | null;
  onboarding_status: string | null;
  interview_status: string | null;
  i9_status: string | null;
  onboarding_docs_status: string | null;
  background_status: string | null;
  checkr_status?: string | null;
  active_and_ready_to_work: boolean;
}

export interface WorkerAddress {
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export interface WorkerOnboardingStep {
  id: string;
  title: string;
  status:
    | 'pending'
    | 'in_progress'
    | 'completed'
    | 'rejected'
    | 'failed'
    | 'skipped';
  completed_at: string | null;
}

export interface WorkerDates {
  approved_at: string | null;
  onboarding_completed_at: string | null;
  denied_at: string | null;
  bypassed_at: string | null;
  tc_accepted_at: string | null;
}

export interface Worker {
  id: number;
  active: boolean;
  user: WorkerUser;
  statuses: WorkerStatuses;
  tax_type: 'w2' | '1099';
  address: WorkerAddress;
  onboarding_steps: WorkerOnboardingStep[];
  dates: WorkerDates;
  created_at: string;
  updated_at: string;
}

export interface WorkerListItem extends Worker {
  avatar_url: string | null;
  average_rating: number | null;
  jobs_worked: number;
  dependability_tier: 'high' | 'medium' | 'low' | 'not_rated';
  accountability_status: 'active' | 'suspended' | 'permanently_removed';
  onboarding_percentage: number;
}

// Extended Worker Detail
export interface WorkerLocation {
  lat: number;
  lng: number;
  timezone: string;
}

export interface WorkerStats {
  jobs_worked: number;
  jobs_dropped: number;
  jobs_failed: number;
  no_show: number;
}

export interface WorkerRatings {
  average_rating: number | null;
  jobs_rating_count: number;
}

export interface WorkerVerification {
  status: string;
  verified_at: string | null;
}

export interface SkillRequestV2 {
  id: number;
  skill_id: number;
  skill_name: string;
  work_experience_completed: boolean;
  assessment_passed: boolean;
  uniform_photo_uploaded: boolean;
  certificate_photo_uploaded: boolean;
  work_experience_completed_at: string | null;
  assessment_passed_at: string | null;
  uniform_photo_uploaded_at: string | null;
  certificate_photo_uploaded_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  rejection_comment: string | null;
  retry: number;
  created_at: string;
  updated_at: string;
}

export interface WorkerCertificate {
  id: number;
  certificate_id: number;
  certificate_name: string;
  certificate_description: string | null;
  certificate_type: number;
  image_url: string | null;
  created_at: string;
}

export interface WorkerUniform {
  id: number;
  uniform_id: number | null;
  uniform_name: string | null;
  uniform_name_es: string | null;
  image_key: string | null;
  image_url: string | null;
  status: string;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkExperience {
  id: number;
  company_name: string;
  start_date: string;
  end_date: string | null;
  is_staffing_agency: boolean;
  duration_months: number | null;
  created_at: string;
}

export interface AssessmentAnswer {
  id: number;
  skill_id: number;
  skill_name: string;
  correct_answers_count: number;
  total_questions: number;
  percentage: number;
  is_passing: boolean;
  total_time_seconds: number;
  answered_at: string;
  created_at: string;
}

export interface CertificatePhoto {
  id: number;
  skill_id: number;
  skill_name: string;
  image_key: string;
  image_url: string | null;
  status: string;
  type: string | null;
  created_at: string;
  updated_at: string;
}

export interface PastJob {
  id: number;
  job_id: number;
  status: string;
  showed: boolean;
  pay_rate: number;
  cost_rate: number;
  tax_type: string;
  dropped_at: string | null;
  dismissed_at: string | null;
  drop_reason: string | null;
  dismiss_reason: string | null;
  has_approved_time_sheet: boolean;
  skill_name: string;
  customer_name: string;
  address: {
    line1: string | null;
    city: string | null;
    state: string | null;
  };
  shift_start_at: string | null;
  shift_end_at: string | null;
  timesheet: {
    id: number;
    approved_minutes: number;
    approved_at: string | null;
    rating: number | null;
  } | null;
  created_at: string;
}

export interface WorkerUniform {
  id: number;
  uniform_id: number | null;
  uniform_name: string | null;
  uniform_name_es: string | null;
  image_key: string | null;
  image_url: string | null;
  status: string;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkExperience {
  id: number;
  company_name: string;
  start_date: string;
  end_date: string | null;
  is_staffing_agency: boolean;
  duration_months: number | null;
  created_at: string;
}

export interface WorkerSkill {
  id: number;
  worker_id: number;
  skill_id: number;
  skill_name: string;
  skill_name_es: string;
  expires_at: string | null;
  renewal_notified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkerSkillsResponse {
  success: boolean;
  data: WorkerSkill[];
}

export interface UpdateWorkerSkillResponse {
  success: boolean;
  data: WorkerSkill;
  message: string;
}

// Consolidated Skills Types
export interface ConsolidatedWorkExperience {
  id: number;
  company_name: string;
  start_date: string;
  end_date: string | null;
  duration_months: number;
  is_staffing_agency: boolean;
  created_at: string;
}

export interface ConsolidatedAssessmentScore {
  total_questions: number;
  correct_answers: number;
  incorrect_answers: number;
  percentage: number;
}

export interface ConsolidatedAssessment {
  id: number;
  passed: boolean;
  passed_at: string | null;
  score: ConsolidatedAssessmentScore | null;
  answers: any;
  created_at: string;
}

export interface ConsolidatedUniform {
  id: number;
  uniform_id: number;
  uniform_name: string | null;
  uniform_name_es: string | null;
  uniform_instructions: string | null;
  uniform_instructions_es: string | null;
  image_url: string | null;
  status: string;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface UniformInfo {
  id: number;
  name: string;
  name_es: string;
  instructions: string;
  instructions_es: string;
}

export interface ConsolidatedCertificate {
  id: number;
  type: string;
  image_url: string | null;
  status: string;
  created_at: string;
  expires_at: string | null;
}

export interface ConsolidatedAiAssessment {
  id: number;
  external_id: string;
  status:
    | 'pending'
    | 'recording'
    | 'transcribing'
    | 'scoring'
    | 'completed'
    | 'failed';
  result_status: 'pending' | 'failed' | 'for_review' | 'passed';
  final_status: 'pending' | 'failed' | 'for_review' | 'passed';
  total_score: number | null;
  language: string;
  dial_count: number;
  is_worker_locked: boolean;
  lock_reason: string | null;
  started_at: string | null;
  completed_at: string | null;
  reviewed_at: string | null;
  reviewed_by_name: string | null;
  created_at: string;
}

export interface ConsolidatedSkillRequest {
  id: number;
  work_experience_completed: boolean;
  assessment_passed: boolean;
  uniform_photo_uploaded: boolean;
  certificate_photo_uploaded: boolean;
  work_experience_completed_at: string | null;
  assessment_passed_at: string | null;
  uniform_photo_uploaded_at: string | null;
  certificate_photo_uploaded_at: string | null;
  work_experiences: ConsolidatedWorkExperience[];
  assessment: ConsolidatedAssessment | null;
  uniforms: ConsolidatedUniform[];
  certificates: ConsolidatedCertificate[];
  retry: number;
  can_retry_assessment: boolean;
  retry_message: string | null;
  assessment_attempt_count: number;
  assessment_retry_eligible_at: string | null;
  assessment_permanently_ineligible: boolean;
  accepted_at: string | null;
  rejected_at: string | null;
  rejection_comment: string | null;
  all_requirements_completed: boolean;
  completion_percentage: number;
}

export interface ConsolidatedWorkerSkill {
  id: number;
  disabled: boolean;
  expires_at: string | null;
  renewal_notified_at: string | null;
  days_until_expiration: number | null;
  expired: boolean;
  requires_renewal: boolean;
  jobs_count: number;
  job_rating_sum: number;
  jobs_worked: number;
  created_at: string;
  updated_at: string;
}

export interface ConsolidatedAssessmentAnswer {
  id: number;
  correct_answers_count: number;
  total_questions: number;
  percentage: number;
  is_passing: boolean;
  total_time_seconds: number;
  answered_at: string;
  created_at: string;
}

export interface ConsolidatedSkill {
  skill_request_id: number | null;
  worker_skill_id: number | null;
  skill_id: number;
  skill_name: string;
  skill_name_es: string;
  skill_image_url: string | null;
  requires_uniform: boolean;
  requires_certificate: boolean;
  requires_assessment: boolean;
  status: 'in_progress' | 'approved' | 'rejected';
  display_status: 'approved' | 'needs_review' | 'in_progress' | 'ineligible';
  requirements_progress: { completed: number; total: number } | null;
  expires_at: string | null;
  skill_request_status: 'accepted' | 'rejected' | 'pending' | null;
  ai_assessment_status:
    | 'pending'
    | 'recording'
    | 'transcribing'
    | 'scoring'
    | 'completed'
    | 'failed'
    | null;
  ai_assessment_result_status:
    | 'pending'
    | 'failed'
    | 'for_review'
    | 'passed'
    | null;
  ai_assessment_final_status:
    | 'pending'
    | 'failed'
    | 'for_review'
    | 'passed'
    | null;
  skill_request: ConsolidatedSkillRequest | null;
  worker_skill: ConsolidatedWorkerSkill | null;
  assessment_answer: ConsolidatedAssessmentAnswer | null;
  ai_assessment: ConsolidatedAiAssessment | null;
  required_certificate_types: string[];
  uniform_info: UniformInfo[] | null;
  needs_review_reasons: string[];
  created_at: string;
  updated_at: string;
}

export interface ConsolidatedSkillsResponse {
  success: boolean;
  data: {
    skills: ConsolidatedSkill[];
    grouped: {
      approved: ConsolidatedSkill[];
      needs_review: ConsolidatedSkill[];
      in_progress: ConsolidatedSkill[];
      ineligible: ConsolidatedSkill[];
    };
    counts: {
      approved: number;
      needs_review: number;
      in_progress: number;
      ineligible: number;
    };
  };
}

export interface ConsolidatedSkillResponse {
  success: boolean;
  data: ConsolidatedSkill;
}

export interface ActiveSuspension {
  id: number;
  status: string;
  suspension_type: string;
  duration_days: number;
  starts_at: string;
  ends_at: string | null;
  worker_visible_reason: string;
}

export interface WorkerComment {
  id: number;
  body: string;
  createdAt: string;
  actor: {
    id: number;
    firstName: string;
    lastName: string;
    middleName: string | null;
  } | null;
}

export interface WorkerReference {
  id: number;
  employer_name: string | null;
  job_title: string | null;
  city: string | null;
  phone: string | null;
  duration: string | null;
}

export interface WorkerDetail extends Worker {
  avatar_url: string | null;
  location: WorkerLocation | null;
  stats: WorkerStats;
  ratings: WorkerRatings;
  verification: WorkerVerification;
  check_employee_id: string | null;
  is_suspended: boolean;
  suspension_type: string | null;
  suspension_ends_at: string | null;
  suspension_worker_visible_reason: string | null;
  active_suspension: ActiveSuspension | null;
  gender: string | null;
  heard_from: string | null;
  owns_car: boolean;
  has_ssn: boolean;
  reenable_date: string | null;
  disable_reason: string | null;
  deactivated_by_user: {
    id: number;
    firstName: string;
    lastName: string;
    middleName: string | null;
  } | null;
  past_jobs: PastJob[];
  comments: WorkerComment[];
  references: WorkerReference[];
  skill_requests: SkillRequestV2[];
  certificates: WorkerCertificate[];
  uniforms: WorkerUniform[];
  work_experiences: WorkExperience[];
  assessment_answers: AssessmentAnswer[];
  certificate_photos: CertificatePhoto[];
}

// Pagination
export interface Pagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// API Responses
export interface WorkersListResponse {
  success: boolean;
  data: WorkerListItem[];
  pagination: Pagination;
}

export interface WorkerDetailResponse {
  success: boolean;
  data: WorkerDetail;
}

// CheckHQ Types
export interface CheckHQOnboard {
  status: 'complete' | 'blocking' | 'pending';
  blocking_steps: string[];
  remaining_steps: string[];
  ssn: string[];
  payment_method: string[];
  workplaces: string[];
}

export interface CheckHQResidence {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface CheckHQData {
  id: string;
  first_name: string;
  last_name: string;
  middle_name: string | null;
  email: string;
  dob: string;
  bank_accounts: any[];
  ssn_last_four: string | null;
  ssn_validation_status: string | null;
  onboard: CheckHQOnboard;
  payment_method_preference: string;
  company: string;
  workplaces: string[];
  primary_workplace: string | null;
  start_date: string;
  termination_date: string | null;
  active: boolean;
  residence: CheckHQResidence;
  w2_electronic_consent_provided: boolean;
  metadata: Record<string, any>;
}

export type CheckHQResponse =
  | {
      available: true;
      employee_id: string;
      type: 'employee' | 'contractor';
      data: CheckHQData;
    }
  | {
      available: false;
      reason?: string;
      employee_id?: string;
      error?: string;
    };

// Zeal Types
export type ZealResponse =
  | {
      available: true;
      employee_id: string;
      taxable_location_id: string | null;
      work_location_id: string | null;
      tnc_date: string | null;
      data: Record<string, any> | null;
    }
  | {
      available: false;
      reason?: string;
      employee_id?: string;
      error?: string;
    };

// Checkr Types
export interface CheckrReport {
  id: string;
  status: string;
  result: string | null;
  adjudication: string | null;
  completed_at: string | null;
  created_at: string;
  package: string;
  candidate_id: string;
  turnaround_time: number;
  eta: string | null;
  [key: string]: any;
}

export interface CheckrCandidate {
  id: string;
  object: string;
  uri: string;
  created_at: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  email: string;
  phone: string;
  zipcode: string;
  dob: string;
  ssn: string;
  driver_license_number: string | null;
  driver_license_state: string | null;
  adjudication: string | null;
  report_ids: string[];
  [key: string]: any;
}

export type CheckrResponse =
  | {
      available: true;
      candidate_id: string;
      checkr_status: string | null;
      candidate: CheckrCandidate;
      reports: CheckrReport[];
    }
  | {
      available: false;
      reason?: string;
      candidate_id?: string;
      checkr_status?: string | null;
      error?: string;
    };

// Accountability Types
export interface AccountabilityCurrentStatus {
  accountability_status: string;
  suspension_ends_at: string | null;
  active_late_drops_count: number;
  active_no_shows_count: number;
}

export interface AccountabilityStatistics {
  total_violations: number;
  total_late_drops: number;
  total_no_shows: number;
  total_suspensions: number;
  total_appeals: number;
  reliability_rate: number;
}

export interface AccountabilityWorker {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  jobs_worked: number;
  jobs_dropped: number;
  no_show: number;
  average_rating: number;
}

export interface DependabilityScore {
  tier: string | null;
  label: string;
  helper_text: string;
  completed_shifts: number;
  active_violations_count: number;
  active_late_drops_count: number;
  active_no_shows_count: number;
  is_suspended: boolean;
}

export interface AccountabilityResponse {
  success: boolean;
  data: {
    worker: AccountabilityWorker;
    current_status: AccountabilityCurrentStatus;
    dependability_score: DependabilityScore | null;
    statistics: AccountabilityStatistics;
  };
}

export interface ExternalsResponse {
  success: boolean;
  worker_id: number;
  checkhq: CheckHQResponse;
  zeal: ZealResponse;
  checkr: CheckrResponse;
}

export interface WorkerSuspensionActionResponse {
  success: boolean;
  message?: string;
  errors?: string[];
}

export interface WorkerActiveStateResponse {
  success: boolean;
  data: {
    worker_id: number;
    active: boolean;
    user_active: boolean;
  };
  errors?: string[];
}

// Skill Types
export interface Skill {
  id: number;
  display_name_en: string;
  display_name_es: string;
  requires_uniform: boolean;
  requires_certificate: boolean;
  requires_assessment: boolean;
}

export interface SkillsResponse {
  success: boolean;
  data: Skill[];
}

// Query Params
export interface WorkersListParams {
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  search?: string;
  step_id?: string;
  step_status?: string;
  i9_status?: string | string[];
  onboarding_status?: string | string[];
  checkr_status?: string | string[];
  onboarding_completed_at?: 'null' | 'present';
  onboarding_completed_from?: string;
  onboarding_completed_to?: string;
  tax_type?: 'w2' | '1099';
  active?: boolean;
  checkr_candidate_status?: string;
  zip?: string;
  radius?: number;
  skill_ids?: number[];
  dependability_tier?: string[];
  accountability_status?: string[];
}

export interface UpdateWorkerProfilePayload {
  first_name: string;
  middle_name: string | null;
  last_name: string;
  email: string;
  date_of_birth: string;
  gender?: string | null;
  language?: string | null;
  secondary_language?: string | null;
  smartphone_type?: string | null;
  heard_from?: string | null;
  owns_car?: boolean;
  has_ssn?: boolean;
  phone_number?: string | null;
}

export interface UpdateWorkerProfileFieldError {
  field: string;
  code: string;
  message: string;
  conflicting_worker?: { id: number; name: string } | null;
}

export interface UpdateWorkerProfileSuccess {
  success: true;
  data: { worker: WorkerDetail };
  message?: string;
}

export interface UpdateWorkerProfileFailure {
  success: false;
  errors: UpdateWorkerProfileFieldError[];
}

export type UpdateWorkerProfileResponse =
  | UpdateWorkerProfileSuccess
  | UpdateWorkerProfileFailure;

export class WorkersV2API {
  /**
   * Get paginated list of workers with filtering
   */
  static async getWorkers(
    params: WorkersListParams = {},
  ): Promise<WorkersListResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.per_page)
      queryParams.append('per_page', params.per_page.toString());
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_direction)
      queryParams.append('sort_direction', params.sort_direction);
    if (params.search) queryParams.append('search', params.search);
    if (params.step_id) queryParams.append('step_id', params.step_id);
    if (params.step_status)
      queryParams.append('step_status', params.step_status);
    if (params.onboarding_completed_at)
      queryParams.append(
        'onboarding_completed_at',
        params.onboarding_completed_at,
      );
    if (params.onboarding_completed_from)
      queryParams.append(
        'onboarding_completed_from',
        params.onboarding_completed_from,
      );
    if (params.onboarding_completed_to)
      queryParams.append(
        'onboarding_completed_to',
        params.onboarding_completed_to,
      );
    if (params.tax_type) queryParams.append('tax_type', params.tax_type);
    if (params.active !== undefined)
      queryParams.append('active', params.active.toString());
    if (params.checkr_candidate_status)
      queryParams.append(
        'checkr_candidate_status',
        params.checkr_candidate_status,
      );
    if (params.zip) queryParams.append('zip', params.zip);
    if (params.radius) queryParams.append('radius', params.radius.toString());

    // Handle array params
    if (params.i9_status) {
      const statuses = Array.isArray(params.i9_status)
        ? params.i9_status
        : [params.i9_status];
      statuses.forEach((s) => queryParams.append('i9_status[]', s));
    }
    if (params.onboarding_status) {
      const statuses = Array.isArray(params.onboarding_status)
        ? params.onboarding_status
        : [params.onboarding_status];
      statuses.forEach((s) => queryParams.append('onboarding_status[]', s));
    }
    if (params.checkr_status) {
      const statuses = Array.isArray(params.checkr_status)
        ? params.checkr_status
        : [params.checkr_status];
      statuses.forEach((s) => queryParams.append('checkr_status[]', s));
    }
    if (params.skill_ids) {
      params.skill_ids.forEach((id) =>
        queryParams.append('skill_ids[]', id.toString()),
      );
    }
    if (params.dependability_tier) {
      params.dependability_tier.forEach((t) =>
        queryParams.append('dependability_tier[]', t),
      );
    }
    if (params.accountability_status) {
      params.accountability_status.forEach((s) =>
        queryParams.append('accountability_status[]', s),
      );
    }

    const response = await axiosRubyClient.get(
      `/api/admin/workers?${queryParams.toString()}`,
    );
    return response.data;
  }

  /**
   * Get worker details by ID
   */
  static async getWorker(workerId: number): Promise<WorkerDetailResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/workers/${workerId}`,
    );
    return response.data;
  }

  /**
   * Update editable worker + user profile fields (email, name, DOB, phone, etc).
   * On HTTP 422 returns a structured failure with per-field errors; the email
   * `taken` error includes a conflicting_worker pointer for inline linking.
   */
  static async updateWorkerProfile(
    workerId: number,
    payload: UpdateWorkerProfilePayload,
  ): Promise<UpdateWorkerProfileResponse> {
    const response = await axiosRubyClient.patch(
      `/api/admin/workers/${workerId}/profile`,
      { profile: payload },
      { validateStatus: (status) => status < 500 },
    );
    return response.data;
  }

  /**
   * Get external service data (CheckHQ, Zeal) for a worker
   */
  static async getWorkerExternals(
    workerId: number,
  ): Promise<ExternalsResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/workers/${workerId}/externals`,
    );
    return response.data;
  }

  /**
   * Sync onboarding steps from CheckHQ
   */
  static async syncFromCheckHQ(workerId: number): Promise<any> {
    const response = await axiosRubyClient.post(
      `/api/admin/workers/${workerId}/sync_from_checkhq`,
    );
    return response.data;
  }

  /**
   * Sync I9/E-Verify status from Zeal
   */
  static async syncFromZeal(workerId: number): Promise<any> {
    const response = await axiosRubyClient.post(
      `/api/admin/workers/${workerId}/sync_from_zeal`,
    );
    return response.data;
  }

  /**
   * Sync background check status from Checkr
   */
  static async syncFromCheckr(workerId: number): Promise<any> {
    const response = await axiosRubyClient.post(
      `/api/admin/workers/${workerId}/sync_from_checkr`,
    );
    return response.data;
  }

  /**
   * Get all worker skills for a worker
   */
  static async getWorkerSkills(
    workerId: number,
  ): Promise<WorkerSkillsResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/worker_skills?worker_id=${workerId}`,
    );
    return response.data;
  }

  /**
   * Update worker skill expiration date
   */
  static async updateWorkerSkillExpiration(
    workerSkillId: number,
    expiresAt: string | null,
  ): Promise<UpdateWorkerSkillResponse> {
    const response = await axiosRubyClient.patch(
      `/api/admin/worker_skills/${workerSkillId}`,
      { expires_at: expiresAt },
    );
    return response.data;
  }

  /**
   * Get consolidated skills for a worker (merges skill_request_v2s and worker_skills)
   */
  static async getConsolidatedSkills(
    workerId: number,
  ): Promise<ConsolidatedSkillsResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/workers/${workerId}/skills/consolidated`,
    );
    return response.data;
  }

  /**
   * Get a single consolidated skill for a worker (finds or creates SkillRequestV2)
   */
  static async getConsolidatedSkill(
    workerId: number,
    skillId: number,
  ): Promise<ConsolidatedSkillResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/workers/${workerId}/skills/${skillId}`,
    );
    return response.data;
  }

  /**
   * Get all active skills
   */
  static async getSkills(): Promise<SkillsResponse> {
    const response = await axiosRubyClient.get(`/api/admin/skills`);
    return response.data;
  }

  /**
   * Get worker accountability overview (dependability, violations, suspensions)
   */
  static async getWorkerAccountability(
    workerId: number,
  ): Promise<AccountabilityResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/workers/${workerId}/accountability`,
    );
    return response.data;
  }

  /**
   * Lift an active worker suspension
   */
  static async liftSuspension(
    suspensionId: number,
    reason: string,
  ): Promise<WorkerSuspensionActionResponse> {
    const response = await axiosRubyClient.post(
      `/api/admin/accountability/suspensions/${suspensionId}/lift`,
      { reason },
    );
    return response.data;
  }

  /**
   * Reactivate a worker account.
   */
  static async reactivateWorker(
    workerId: number,
  ): Promise<WorkerActiveStateResponse> {
    const response = await axiosRubyClient.post(
      `/api/admin/workers/${workerId}/reactivate`,
    );
    return response.data;
  }

  /**
   * Deactivate a worker account.
   */
  static async deactivateWorker(
    workerId: number,
    reason: string,
  ): Promise<WorkerActiveStateResponse> {
    const response = await axiosRubyClient.post(
      `/api/admin/workers/${workerId}/deactivate`,
      { reason },
    );
    return response.data;
  }

  /**
   * Add a comment to a worker
   */
  static async addComment(
    workerId: number,
    body: string,
  ): Promise<{ success: boolean; data: WorkerComment }> {
    const response = await axiosRubyClient.post(
      `/api/admin/workers/${workerId}/add_comment`,
      { body },
    );
    return response.data;
  }
}
